import moment from 'moment';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  Coding,
  ContactPoint,
  Identifier,
  Reference,
  CodeSystem,
  Extension,
  Period,
  Practitioner,
  Address,
  ActivityDefinition,
  FhirResource,
  CareTeamParticipant,
  EpisodeOfCare,
} from 'src/careflow/ph-core/fhir-client/resources';
import {
  GOAL_RELATIONSHIP_EXTENSION_URL,
  NPI_IDENTIFIER_SYSTEM,
  PROVIDER_LINK_IDENTIFIER,
} from 'src/careflow/helpers/constants';
import { trimMultipleWhiteSpaces } from './string';
import { WrappedPractitioner } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedPractitionerRole } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { WrappedRelatedPerson } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { QualificationType, SimpleQualification } from 'src/@types/crs/providerDirectory';
import { PROVIDER_CERTIFICATION_IDENTIFIER } from 'src/config';
import { TaskSelectedMemberValue } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { capitalize, every, isEmpty, isPlainObject } from 'lodash';
import { getFullName } from 'src/careflow/common/common-utils';
import { getResourceByReference } from 'src/careflow/helpers/incident';

export interface SplitReference {
  resourceType: string;
  uuid: string;
}

export function getIdFromReference(reference: string) {
  if (reference) {
    const fhirIdArray = reference?.split('/');
    return fhirIdArray?.length > 0 ? fhirIdArray.slice(-1)[0] : null;
  }
  return null;
}

export function getFhirIdFromEntity(fhirId: string) {
  const fhirIdArray = fhirId?.split('/');
  return fhirIdArray?.length > 0 ? fhirIdArray.slice(-1)[0] : null;
}

export function referenceEquals(
  reference: Reference,
  resource:
    | Reference
    | {
        resourceType: string;
        id?: string;
      }
) {
  const isResource = 'resourceType' in resource;
  const [resourceType, id] = isResource
    ? [resource.resourceType, resource.id]
    : resource.reference?.split('/') || [resource.type, resource.id];

  return (
    reference.reference === `${resourceType}/${id}` ||
    (reference.type === resourceType && reference.id === id)
  );
}

export function setStatus(statusList: any, statusValue: any) {
  const statusLabel = statusList
    .filter((e: any) => e.value === statusValue)
    .map((s: any) => s.label);
  return statusLabel;
}

export function getAddressFormated(address: any) {
  const objectAddress: { [key: string]: string } = {};
  for (let i: number = 0; i < address?.line?.length; i++) {
    objectAddress[`line_${i}`] = address?.line?.[i];
  }

  delete address?.line;
  if (address?.period) {
    address.period.start = moment.utc(new Date(address.period.start)).format('MM/DD/YYYY');
    address.period.end = moment.utc(new Date(address.period.end)).format('MM/DD/YYYY');
  }

  address = { ...address, ...objectAddress };

  return address;
}

export function mergeLineAddress(address: any) {
  const line = [address?.line_0, address?.line_1];

  delete address.line_0;
  delete address.line_1;

  const originalAddress = address;
  address = { ...originalAddress, line };

  return address;
}

export function getPrimaryPhone(telecom: any): ContactPoint | null {
  let mainPhone = null;
  if (telecom && telecom?.length > 0) {
    const phones = telecom.filter((t: any) => t?.system === 'phone');
    if (phones && phones?.length > 0) {
      mainPhone = phones[0];
    }
  }
  return mainPhone;
}

export function getIdentifier(
  patient: any,
  identifierCode: string,
  valueSet?: WrappedValueSet | null,
  code?: string
) {
  const getConceptIdentifier = valueSet?.compose?.include?.reduce<Identifier[]>(
    (includes, include) => {
      if (!include?.concept?.length) return includes;

      return [
        ...includes,
        ...include.concept.map(({ code, display, extension }) => ({
          ...(extension?.[0]?.extension?.length && { system: extension[0].extension[0].valueUri }),
          type: {
            coding: [
              {
                code,
                system: include?.system,
              },
            ],
            ...(display && { text: display }),
          },
        })),
      ];
    },
    []
  );

  const identifierType = getConceptIdentifier?.find((e) => e?.type?.coding?.[0]?.code === code);

  const value =
    patient?.identifier
      ?.filter((identifier: any) => identifier.type.toLowerCase() === identifierCode.toLowerCase())
      .map((e) => ({
        ...(!identifierType?.system && e?.system && { system: e?.system }),
        ...identifierType,
        ...(e.use && { use: e.use }),
        value: e.value,
      }))[0] || null;

  return value;
}

export function getSplitReference(value: string): SplitReference | null {
  if (!value) return null;
  const splitedValue = value.split('/');
  const splitRe: SplitReference = {
    resourceType: splitedValue[0],
    uuid: splitedValue[1],
  };

  return splitRe;
}

export const getIdentifierByType = (identifier: Identifier[] | undefined, typeText: string) =>
  identifier?.find((item) => item?.type?.text === typeText)?.value;

export const getTelecomBySystem = (telecom: ContactPoint[] | null | undefined, system: string) =>
  telecom?.find?.(({ system: telecomSystem }) => system === telecomSystem);

export const getTelecomsBySystem = (
  telecom: ContactPoint[] | null | undefined,
  system: string
): ContactPoint[] =>
  telecom?.filter?.(({ system: telecomSystem }) => system === telecomSystem) ?? [];

export const getTelecomsSystems = (
  telecom: ContactPoint[] | null | undefined
): ContactPoint['system'][] => [...new Set(telecom?.map?.(({ system }) => system) ?? [])];

export function phoneFormat(phoneString: any) {
  let cleaned = ('' + phoneString).replace(/\D/g, '');
  let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return match[1] + '-' + match[2] + '-' + match[3];
  }

  return phoneString;
}

export function getAgeGroup(patientAgeInMonths: number, codeSystem: CodeSystem) {
  const concept = codeSystem?.property?.filter((item) => {
    const [minStr, maxStr] = item?.code?.split('-') || [null, null];
    const min = Number(minStr);
    const max = Number(maxStr);

    return (
      patientAgeInMonths !== null &&
      !isNaN(min) &&
      !isNaN(max) &&
      patientAgeInMonths >= min &&
      patientAgeInMonths <= max
    );
  });
  return concept;
}

export function setSILDisplay(sil: string) {
  let result: string = '';

  switch (sil) {
    case '1':
      result = 'Service Integration Level 1';
      break;
    case '2':
      result = 'Service Integration Level 2';
      break;
    case '3':
      result = 'Service Integration Level 3';
      break;
    default:
      result = sil;
  }

  return result;
}

export const getGenderIdentityExtension = (
  { code, display, system }: Coding,
  configurations: any
): Extension => {
  const show = system === 'ValueSet/administrative-gender';
  const URLS_GENDER_IDENTITY = [
    'ValueSet/us-core-birthsex',
    configurations?.genderIdentityExtensionDefinitionUrl,
    configurations?.genderIdentityCodeSystemUrl,
  ];
  return {
    url: show ? URLS_GENDER_IDENTITY[0] : configurations?.genderIdentityExtensionDefinitionUrl,
    valueCodeableConcept: {
      coding: [
        {
          code,
          ...(!show && { display }),
          system: system ?? configurations?.genderIdentityCodeSystemUrl,
        },
      ],
      ...(!show && { text: display }),
    },
  };
};

export const checkIfIdentifierPeriodIsValid = ({ start, end }: Period): boolean => {
  const periodStart = moment(start ?? null);

  const periodEnd = moment(end ?? '9999-12-31');

  return !!(
    periodStart.isValid() &&
    periodEnd.isValid() &&
    moment().isBetween(periodStart, periodEnd, undefined, '[]')
  );
};

export const getValidProviderDirectoryLinkIdentifiers = (
  identifiers: Identifier[],
  configurations: any
): Identifier[] => {
  const providerDirectoryLinks =
    identifiers?.filter?.(
      ({ type, system }) =>
        type?.coding?.[0]?.code === PROVIDER_LINK_IDENTIFIER(configurations).type.coding[0].code &&
        system === PROVIDER_LINK_IDENTIFIER(configurations).system
    ) ?? [];

  return providerDirectoryLinks.reduce<Identifier[]>(
    (validProviderDirectoryLinks, providerDirectoryLink) => {
      const isVerified = checkIfIdentifierPeriodIsValid(providerDirectoryLink?.period ?? {});
      if (isVerified) return [...validProviderDirectoryLinks, providerDirectoryLink];
      return validProviderDirectoryLinks;
    },
    []
  );
};

export const getPractitionerProviderDirectoryId = (
  practitioner: Practitioner | undefined | null,
  configurations: any
): string | null => {
  const providerDirectoryIdentifiers = getValidProviderDirectoryLinkIdentifiers(
    practitioner?.identifier ?? [],
    configurations
  );
  if (providerDirectoryIdentifiers?.[0]?.value) return providerDirectoryIdentifiers[0].value;

  return null;
};

export const getNPIFromIdentifier = (identifiers: Identifier[]): string =>
  identifiers.find(({ system }) => system === NPI_IDENTIFIER_SYSTEM)?.value ?? '';

export const formatAddress = (address: Address) => {
  if (!address) return '';
  const addr: any = [];
  if (address?.line) addr.push(address.line.filter((l) => l).join(', '));
  if (address?.city) addr.push(address.city);
  if (address?.state) addr.push(address.state);
  if (address?.postalCode) addr.push(address.postalCode);
  if (address?.use) {
    addr.push(`(${capitalize(address.use)})`);
  } else {
    addr.push(`(Other)`);
  }
  return addr.join(', ');
};

export const formatUSAddress = (address: Address): string => {
  let formattedAddress = '';

  if (address?.line && address?.line?.length > 0) {
    formattedAddress += address.line.join(', ') + '\n';
  }

  if (address?.city || address?.state || address?.postalCode) {
    formattedAddress +=
      `${address.city || ''}${address.city && address.state ? ', ' : ''}${address.state || ''} ${
        address.postalCode || ''
      }`.trim() + '\n';
  }

  if (address?.country) {
    formattedAddress += address.country;
  }

  return formattedAddress.trim();
};

export const getTelecomValues = (
  system: ContactPoint['system'],
  telecom: ContactPoint[]
): string[] =>
  telecom.filter((telecom) => telecom.system === system).map(({ value }) => value ?? '') ?? [];

export const verifyIfPractitionerIsVerified = (
  practitioner: Practitioner | undefined | null,
  configurations: any
): boolean => {
  const providerDirectoryIdentifiers = getValidProviderDirectoryLinkIdentifiers(
    practitioner?.identifier ?? [],
    configurations
  );
  return !!providerDirectoryIdentifiers.length;
};

export const getValuesFromReference = (
  reference: string | null | undefined
): [string | null, string | null] => {
  if (!reference) return [null, null];
  const splitted = reference.split('/');
  return [splitted?.[0] ?? null, splitted?.[1] ?? null];
};
export const getDynamicValueExpressionByPath = (
  dynamicValues: ActivityDefinition['dynamicValue'],
  path: string
): string | undefined =>
  dynamicValues?.find?.(({ path: dynamicValuePath }) => path === dynamicValuePath)?.expression
    ?.expression;

export const getResourcesAndRelatedResources = (
  resources: FhirResource[] | null | undefined,
  resourceType: string
) => [
  getDistinctResources(resources?.filter?.(({ resourceType: rt }) => resourceType === rt) ?? []),
  getDistinctResources(resources?.filter?.(({ resourceType: rt }) => resourceType !== rt) ?? []),
];

export const getDistinctResources = (resources: FhirResource[]) =>
  resources.reduce<FhirResource[]>((distinctResources, resource) => {
    if (distinctResources.find(({ id }) => resource.id === id)) return distinctResources;
    return [...distinctResources, resource];
  }, []);

export const orderTelecomByRank = (telecom: ContactPoint[] | null | undefined) =>
  telecom?.sort?.(({ rank: rankA }, { rank: rankB }) => (rankA ?? 0) - (rankB ?? 0)) ?? [];

export const getExtensionArrayByURLs = (resource: any, urls: string[]): Coding[] | any => {
  let array: any[] = [];

  const filter = resource?.extension
    ? resource.extension.filter((e) => urls.some((x) => x === e?.url))
    : null;

  filter?.forEach((obj) => {
    if (obj.extension) {
      obj.extension.forEach((ext) => {
        if (ext.valueCoding) {
          array.push(ext.valueCoding);
        }
      });
    }

    if (obj.valueCode) {
      array.push(obj);
    }

    if (obj.valueCodeableConcept && obj.valueCodeableConcept.coding) {
      array.push(...obj.valueCodeableConcept.coding);
    }
  });

  return array;
};

export const getReferenceId = (reference: string | null | undefined) =>
  reference?.split?.('/')?.[1];

export const getReferenceType = (reference: string | null | undefined) =>
  reference?.split?.('/')?.[0];

export const getReferenceDisplayByResource = (
  resource: FhirResource,
  includedResources: FhirResource[]
) => {
  let display = '';
  switch (resource.resourceType) {
    case 'Practitioner':
      display = trimMultipleWhiteSpaces(
        (resource as WrappedPractitioner)?.getFullName?.() ?? ''
      ).trim();
      break;
    case 'Organization':
      display = (resource as WrappedOrganization)?.name ?? '';
      break;
    case 'PractitionerRole':
      const practitionerRole = resource as WrappedPractitionerRole;

      const practitionerName = practitionerRole?.getPractitionerName?.(
        (includedResources ?? []) as WrappedPractitioner[]
      );
      const roleName = practitionerRole?.getRoleName?.();
      const organizationName = practitionerRole?.getOrganizationName?.(
        (includedResources ?? []) as WrappedOrganization[]
      );
      display = [practitionerName, roleName, organizationName]
        .filter((data) => !!data?.trim?.())
        .join(' | ');
      break;
    case 'RelatedPerson':
      display = (resource as WrappedRelatedPerson)?.getFullName?.() ?? '';
      break;
    case 'Patient':
      display = (resource as WrappedPatient)?.getFullName?.() ?? '';
      break;
  }

  return display;
};

export const getGoalRelationExtensionPayload = (type: Coding, goalId: string): Extension => ({
  url: GOAL_RELATIONSHIP_EXTENSION_URL,
  extension: [
    {
      url: 'type',
      valueCodeableConcept: {
        coding: [type],
      },
    },
    {
      url: 'target',
      valueReference: {
        reference: `Goal/${goalId}`,
      },
    },
  ],
});

export const getQualificationTypeFromIdentifier = (
  code: string,
  display: string,
  configurations: any
): SimpleQualification['type'] => {
  switch (code) {
    case PROVIDER_CERTIFICATION_IDENTIFIER(configurations?.providerDirectoryBaseUrl)?.type.coding[0]
      .code:
      return QualificationType.CERTIFICATION;
    case configurations?.providerLicenseIdentifierCodingCode:
      return QualificationType.LICENSE;
    case configurations?.providerClassificationIdentifierCodingCode:
      return QualificationType.TAXONOMY;
    default:
      return display ?? 'Other';
  }
};

export const getIncludedResources = (careTeam: any, includeMembersInactives: any) => {
  if (!careTeam?.resource?.participant?.length) return [];

  const careTeamIncludeInactive = includeMembersInactives?.find((e) => e === careTeam?.resource?.id)
    ? true
    : false;

  if (careTeamIncludeInactive) return careTeam?.includedResources;

  const participantValidPeriod = careTeam.resource.participant.reduce(
    (resourceIds: string[], participant: CareTeamParticipant) => {
      const isValidPeriod = participant?.period
        ? checkIfIdentifierPeriodIsValid(participant.period)
        : true;

      if (isValidPeriod && participant?.member?.reference) {
        const memberId = getReferenceId(participant.member.reference);
        if (memberId) {
          resourceIds.push(memberId);
        }
      }

      return resourceIds;
    },
    [] as string[]
  );

  const includedResourcesFiltered = careTeam?.includedResources?.filter((includedResource) =>
    participantValidPeriod?.some(
      (e) => e === includedResource?.resource?.id || includedResource?.id
    )
  );

  return includedResourcesFiltered;
};

export const getWrappedByReference = async (
  reference: Reference | undefined
): Promise<TaskSelectedMemberValue | null> => {
  if (!reference) return null;
  let referred: TaskSelectedMemberValue | null = null;
  const resource = await getResourceByReference(reference?.reference ?? '');
  if (resource.id) {
    switch (resource.resourceType) {
      case 'Practitioner':
        referred = {
          id: resource.id,
          resourceType: resource.resourceType,
          getFullName: () => getFullName(resource.name ?? null) ?? '',
        } as WrappedPractitioner;
        break;
      case 'Patient':
        referred = {
          id: resource.id,
          resourceType: resource.resourceType,
          getFullName: () => getFullName(resource.name ?? null) ?? '',
        } as WrappedPatient;
        break;
      case 'PractitionerRole':
        referred = {
          id: resource.id,
          resourceType: resource.resourceType,
          getPractitionerName: () =>
            resource?.practitioner?.display && resource?.code?.[0]?.coding?.[0]?.display
              ? `${resource?.practitioner?.display} | ${resource?.code?.[0]?.coding?.[0]?.display}`
              : '',
        } as WrappedPractitionerRole;
        break;
      case 'Organization':
        referred = {
          id: resource.id,
          resourceType: resource.resourceType,
          name: resource?.name ?? '',
        } as WrappedOrganization;
        break;
      case 'RelatedPerson':
        referred = {
          id: resource.id,
          resourceType: resource.resourceType,
          getFullName: () => getFullName(resource.name ?? null) ?? '',
        } as WrappedRelatedPerson;
        break;
    }
  }
  return referred;
};

export const deepIsEmpty = (value: any): boolean =>
  isEmpty(value) ||
  (isPlainObject(value) && every(value, deepIsEmpty)) ||
  (Array.isArray(value) && every(value, deepIsEmpty));

export function findInvalidTaskOutputs(
  taskOutput: any[]
): Array<{ index: number; invalidFields: ('type' | 'value')[] }> {
  return taskOutput.reduce<Array<{ index: number; invalidFields: ('type' | 'value')[] }>>(
    (invalid, out, index) => {
      const invalidFields: ('type' | 'value')[] = [];

      const typeInvalid = !out?.type || Object.keys(out.type).length === 0 || out.type === '';
      if (typeInvalid) {
        invalidFields.push('type');
      }

      const hasValue = Object.keys(out ?? {}).some(
        (k) => k.startsWith('value') && out[k] != null && out[k] !== ''
      );
      if (!hasValue) {
        invalidFields.push('value');
      }

      if (invalidFields.length > 0) {
        invalid.push({ index, invalidFields });
      }

      return invalid;
    },
    []
  );
}

export const getEOCLastByCode = (eoc: EpisodeOfCare[]) => {
  if (!eoc) return [];

  const result = Object.values(
    eoc.reduce<Record<string, { start: string; label: string }>>((acc, item) => {
      const code = item?.type?.[0]?.coding?.[0]?.code;
      const start = item?.period?.start;

      if (!code || !start) return acc;

      if (!acc[code] || new Date(start) > new Date(acc[code].start)) {
        const title = item?.type?.[0]?.text;
        let firstTypeCodingDisplay: string | undefined;

        item?.type?.some((t) => {
          const codingDisplay = t?.coding?.find((cod) => cod.display);
          if (codingDisplay) {
            firstTypeCodingDisplay = codingDisplay.display;
            return true;
          }
          return false;
        });

        let label = '';
        if (title) {
          label = `${title} : ${item.status}`;
        } else if (firstTypeCodingDisplay) {
          label = `${firstTypeCodingDisplay} : ${item.status}`;
        } else {
          label = `- : ${item.status}`;
        }

        acc[code] = { start, label };
      }

      return acc;
    }, {})
  ).map((item) => item.label);

  return result;
};

export function getValueX(output: Record<string, any>) {
  if (!output) return undefined;

  const key = Object.keys(output).find((k) => k.startsWith('value'));
  if (!key) return undefined;

  const setOuputKey = typeof output[key] === 'string' ? output[key] : JSON.stringify(output[key]);

  return {
    key,
    value: setOuputKey,
  };
}

export const identifierByConfig = (identifiers: Identifier[] | null, list: any) => {
  const idents = (identifiers ?? [])?.reduce<any[]>((acc, item) => {
    const { icon, system, code, properties } =
      list?.identifiers?.find(
        (i) => i?.system === item?.system || i?.code === item?.type?.coding?.[0]?.code
      ) ?? {};

    if (item?.value && (system || code)) {
      acc.push({
        icon: icon ? icon : 'emojione-monotone:id-button',
        value: item?.value,
        tooltip: item?.type?.text ?? item?.type?.coding?.[0]?.code ?? '',
        properties: {
          marginRight: 9,
          color: '#919eab',
          ...properties,
        },
      });
    }
    return acc;
  }, []);
  return idents;
};
